import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ElementType } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Manrope typography roles for the Afterform interface.
 *
 * Polymorphic via `as`; sensible semantic defaults per variant (override for
 * correct heading levels).
 */
export const textVariants = cva("", {
  variants: {
    variant: {
      display:
        "font-sans text-[clamp(28px,6vw,34px)] font-semibold leading-[1.14] tracking-[-0.028em] text-ink text-balance",
      projectTitle:
        "font-sans text-[34px] font-semibold leading-[1.14] tracking-[-0.028em] text-ink text-balance",
      sectionTitle:
        "font-sans text-[19px] font-semibold leading-[1.32] tracking-[-0.016em] text-ink text-balance",
      subhead: "font-sans text-[15px] font-normal leading-[1.7] text-ink-soft text-pretty",
      wordmark: "font-sans text-[22px] font-semibold tracking-[-0.02em] text-ink",
      h4: "font-sans text-[19px] font-semibold leading-[1.32] tracking-[-0.016em] text-ink",
      lede: "font-sans text-[15px] font-normal leading-[1.7] text-ink-soft text-pretty",
      userBody: "font-sans text-[15px] font-normal leading-[1.7] text-ink",
      body: "font-sans text-[15px] leading-[1.7] text-ink-soft",
      label:
        "font-sans text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted",
      fieldLabel:
        "font-sans text-[12.5px] font-medium leading-[1.4] tracking-[0.02em] text-ink",
      status:
        "font-sans text-xs font-semibold leading-[1.5] tracking-[0.035em]",
      numeric:
        "font-sans text-[26px] font-semibold leading-[1.2] tracking-[-0.022em] tabular-nums text-ink",
      ui: "font-sans text-[13px] font-semibold tracking-[0.005em] text-ink",
    },
  },
  defaultVariants: { variant: "body" },
});

const defaultTag: Record<NonNullable<VariantProps<typeof textVariants>["variant"]>, ElementType> = {
  display: "h1",
  projectTitle: "h1",
  sectionTitle: "h2",
  subhead: "p",
  wordmark: "span",
  h4: "h4",
  lede: "p",
  userBody: "p",
  body: "p",
  label: "span",
  fieldLabel: "label",
  status: "span",
  numeric: "span",
  ui: "span",
};

type TextProps<E extends ElementType> = {
  as?: E;
} & VariantProps<typeof textVariants> &
  Omit<ComponentPropsWithoutRef<E>, "as" | keyof VariantProps<typeof textVariants>>;

export function Text<E extends ElementType = "p">({
  as,
  className,
  variant,
  ...props
}: TextProps<E>) {
  const Comp = (as ?? defaultTag[variant ?? "body"]) as ElementType;
  return <Comp className={cn(textVariants({ variant }), className)} {...props} />;
}
